import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { useApp } from "@/context/AppContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { LogIn, UserPlus, LogOut, QrCode, Loader2, ShieldCheck, Clock, Building2 } from "lucide-react";

export default function Account() {
  const { t } = useApp();
  const a = t.accountP;
  const [member, setMember] = useState(null);
  const [ready, setReady] = useState(false);
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [passes, setPasses] = useState([]);

  const loadPasses = useCallback(() => {
    api.get("/account/passes").then(({ data }) => setPasses(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/account/me")
      .then(({ data }) => { setMember(data); loadPasses(); })
      .catch(() => setMember(null))
      .finally(() => setReady(true));
  }, [loadPasses]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = mode === "register" ? form : { email: form.email, password: form.password };
      const { data } = await api.post(`/account/${mode}`, payload);
      setMember(data);
      toast.success(mode === "register" ? a.created : a.welcome);
      loadPasses();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const logout = async () => {
    try { await api.post("/account/logout"); } catch (e) { console.error(e); }
    setMember(null);
    setPasses([]);
    toast.success(a.loggedOut);
  };

  if (!ready) {
    return <div className="min-h-[60vh] grid place-items-center text-teal-600 font-mono text-sm">{a.loading}</div>;
  }

  return (
    <div className="min-h-[70vh] bg-[#E9F5F4]/40 py-14">
      <div className="oj-container max-w-3xl">
        {!member ? (
          <div className="oj-card p-8 max-w-md mx-auto" data-testid="member-auth-card">
            <div className="grid place-items-center w-12 h-12 rounded-xl bg-teal-50 border border-teal-200 mx-auto mb-4">
              <ShieldCheck className="w-6 h-6 text-teal-600" />
            </div>
            <h1 className="text-2xl font-extrabold text-[#0B3B39] text-center">{a.memberAccount}</h1>
            <p className="text-sm text-slate-600 text-center mt-1 mb-6">{a.sub}</p>

            <div className="grid grid-cols-2 gap-2 mb-6">
              <button onClick={() => setMode("login")} data-testid="member-tab-login"
                className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${mode === "login" ? "bg-teal-600 text-white shadow" : "bg-teal-50 text-slate-600 border border-teal-200"}`}>
                {a.signIn}
              </button>
              <button onClick={() => setMode("register")} data-testid="member-tab-register"
                className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${mode === "register" ? "bg-teal-600 text-white shadow" : "bg-teal-50 text-slate-600 border border-teal-200"}`}>
                {a.register}
              </button>
            </div>

            <form onSubmit={submit} className="space-y-3" data-testid="member-auth-form">
              {mode === "register" && (
                <input required minLength={2} placeholder={a.namePh} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="oj-input" data-testid="member-name" />
              )}
              <input required type="email" placeholder={a.emailPh} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="oj-input" data-testid="member-email" />
              <input required type="password" minLength={8} placeholder={a.passPh} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="oj-input" data-testid="member-password" />
              <button disabled={busy} className="oj-btn-gold w-full !py-3" data-testid="member-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "register" ? <UserPlus className="w-4 h-4" /> : <LogIn className="w-4 h-4" />}
                {mode === "register" ? a.createAccount : a.signIn}
              </button>
            </form>
          </div>
        ) : (
          <div data-testid="member-dashboard">
            <div className="oj-card p-7 mb-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="grid place-items-center w-14 h-14 rounded-full bg-teal-700 text-white text-xl font-extrabold">{member.name?.[0]?.toUpperCase()}</div>
                <div>
                  <h1 className="text-xl font-extrabold text-[#0B3B39]" data-testid="member-name-display">{member.name}</h1>
                  <p className="text-sm text-slate-600">{member.email}</p>
                </div>
              </div>
              <button onClick={logout} className="oj-btn-ghost !text-sm !text-teal-700 !border-teal-300" data-testid="member-logout">
                <LogOut className="w-4 h-4" /> {a.logout}
              </button>
            </div>

            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-extrabold text-[#0B3B39]">{a.myPasses}</h2>
              <Link to="/access-portal" className="text-sm font-semibold text-teal-700 hover:text-amber-600" data-testid="member-new-pass-link">{a.newPass}</Link>
            </div>
            {passes.length === 0 ? (
              <div className="oj-card p-8 text-center text-slate-500 text-sm" data-testid="member-no-passes">
                {a.noPasses}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {passes.map((p) => {
                  const expired = new Date(p.expires_at) < new Date();
                  return (
                    <div key={p.id} className="oj-card p-5" data-testid={`member-pass-${p.id}`}>
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                          <div className="font-mono font-bold text-[#0B3B39]">{p.pass_code}</div>
                          <div className="text-xs text-slate-600 flex items-center gap-1 mt-1"><Building2 className="w-3.5 h-3.5" /> {p.company}</div>
                          <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Clock className="w-3.5 h-3.5" /> {a.validTill} {new Date(p.expires_at).toLocaleString()}</div>
                        </div>
                        <span className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase ${expired ? "bg-red-50 text-red-600 border border-red-200" : "bg-teal-50 text-teal-700 border border-teal-200"}`}>
                          {expired ? a.expired : a.active}
                        </span>
                      </div>
                      <div className="bg-white border border-teal-100 rounded-lg p-3 grid place-items-center">
                        <QRCodeSVG value={p.id} size={110} fgColor="#014845" />
                      </div>
                      <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-1"><QrCode className="w-3.5 h-3.5" /> {a.showQr}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
